/* eslint @typescript-eslint/naming-convention: 0 */
import { sendWabunkaGADynamicEvent } from '@/wabunka-src/common/utils/googleAnalytics/googleAnalytics';
import { sanitizeSensitiveQuery } from '@/wabunka-src/common/utils/url/sanitizeSensitiveQuery';

/** page_view の送出に必要なルート情報。vue-router へ依存させないため構造的に受ける */
export interface PageViewRoute {
  path: string;
  name?: string | symbol | null;
}

/** page_view の送出に必要な router の最小面。vue-router へ依存させないため構造的に受ける */
export interface PageViewRouter {
  isReady: () => Promise<unknown>;
  currentRoute: { value: PageViewRoute };
  afterEach: (guard: (to: PageViewRoute, from: PageViewRoute, failure?: unknown) => void) => unknown;
}

export interface WabunkaPageViewParams {
  page_path: string;
  page_title?: string;
  page_location: string;
}

/** canonical が末尾スラッシュ付きのため、揃えないと GA4 で /faq と /faq/ に割れる */
const normalizePath = (path: string): string => (path === '/' ? path : path.replace(/\/$/, ''));

/**
 * page_view を送るかどうか。パスが変わらない遷移では送らない。
 *
 * 体験詳細はフォーム初期化後にクエリだけを落とす replace を行うため、パスで見ないと同一ページで2回送る。
 * page_view は Meta ViewContent の発火起点でもあり、二重送出はそのまま広告タグの二重発火になる。
 * 比較は page_path と同じ正規化後の値で行う。揃えないと末尾スラッシュ違いの遷移で同一 page_path が2回送られる
 */
export const shouldSendWabunkaPageView = (to: PageViewRoute, from: PageViewRoute): boolean => normalizePath(to.path) !== normalizePath(from.path);

/**
 * vue-gtag が自動送信していた page_view のパラメータを再現する。
 *
 * - page_title はルート名。GA4 の既存データがこの値で蓄積されており、document.title へ変えると計測値が総入れ替えになる
 * - page_path は末尾スラッシュを落とす。canonical が末尾スラッシュ付きのため、落とさないと /faq と /faq/ に割れる
 * - page_location は現在 URL から秘匿すべきクエリを除いた値。vue-gtag は utm を剥がしていたが、GA4 が自前で解釈するため残す
 */
export const buildWabunkaPageViewParams = (route: PageViewRoute, href: string): WabunkaPageViewParams => {
  const pageTitle = route.name?.toString();
  return {
    page_path: normalizePath(route.path),
    ...(pageTitle ? { page_title: pageTitle } : {}),
    page_location: sanitizeSensitiveQuery(href),
  };
};

/** page_view も他イベントと同じ送信口を通し、持ち越しリセットを一様に適用する */
export const sendWabunkaGAPageView = (params: WabunkaPageViewParams) => {
  sendWabunkaGADynamicEvent('page_view', { ...params });
};

/**
 * ルート遷移に page_view の送出を紐づける。
 *
 * 初回は isReady の解決後に1回送り、その後に afterEach を登録する。登録を先にすると初回ナビゲーションでも
 * 発火し、初回送出と二重になる。
 *
 * isReady は初回ナビゲーションが失敗すると reject する。その場合も afterEach の登録だけは通す。
 * 通さないと以降そのセッションの page_view が全ページで欠落する。
 */
export const registerWabunkaPageViewTracking = (router: PageViewRouter, getHref: () => string): Promise<void> => {
  const send = (route: PageViewRoute) => sendWabunkaGAPageView(buildWabunkaPageViewParams(route, getHref()));
  const registerAfterEach = () => {
    router.afterEach((to, from, failure) => {
      // 失敗したナビゲーションでも afterEach は呼ばれる。この場合 URL は更新されないため、
      // 送ると到達していないページの page_view が遷移前の page_location 付きで飛ぶ
      if (failure) return;
      if (shouldSendWabunkaPageView(to, from)) send(to);
    });
  };

  return router.isReady().then(
    () => {
      send(router.currentRoute.value);
      registerAfterEach();
    },
    () => registerAfterEach(),
  );
};
